import { FormField, Input } from "@design";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { ConnectorSetting } from "@/api";
import { Text } from "@/components/Text";

/** Inputs for a connector's declared settings; empty = use the connector's default. */
export function SettingValues({
  settings,
  values,
  onChange,
}: Readonly<{
  settings: ConnectorSetting[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
}>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.connectors" });
  if (settings.length === 0) return null;
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1">
        <Text as="span" variant="body-sm">
          {t("values")}
        </Text>
      </legend>
      <Text variant="caption" tone="tertiary">
        {t("valuesHint")}
      </Text>
      {settings.map((setting) => (
        <SettingInput
          key={setting.name}
          setting={setting}
          value={values[setting.name] ?? ""}
          onChange={(v) => onChange(setting.name, v)}
        />
      ))}
    </fieldset>
  );
}

function SettingInput({
  setting,
  value,
  onChange,
}: Readonly<{ setting: ConnectorSetting; value: string; onChange: (value: string) => void }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.connectors" });
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <FormField label={setting.label} htmlFor={id} required={Boolean(setting.required && !setting.default)}>
        <Input
          id={id}
          value={value}
          placeholder={setting.default ?? undefined}
          onChange={(e) => onChange(e.target.value)}
        />
      </FormField>
      {(setting.description || setting.default) && (
        <Text variant="caption" tone="tertiary">
          {[setting.description, setting.default && t("defaultIs", { value: setting.default })]
            .filter(Boolean)
            .join(" · ")}
        </Text>
      )}
    </div>
  );
}
